<script setup lang="ts">
// Viewpart for the `workspace_memory_search` / `workspace_memory_about_user`
// tools: renders the agent's recalled facts as a compact card list (the fact
// text + a type chip + when it was learned). Registered in lib/toolRenderers.ts;
// rendered by ToolViewparts below the agent's prose, collapsed by default via
// the shared Fold so it doesn't pollute the thread.
//
// Reads tool.outputFull - shape `{ hits: [FactEntry] }` (search) or
// `{ facts: [FactEntry] }` (about_user); FactEntry = { fact, fact_type,
// asserted_at, ... } (crates/hq-mcp-memory MemorySearchOutput). These are
// the agent's own asserted memories, read-only context - no link. fact renders
// as TEXT (Vue escapes - no HTML injection).
import { computed } from 'vue';
import Fold from '../Fold.vue';
import { useIcons } from '../../icons';
import { formatDate } from '../../lib/datetime';
import type { ToolStep } from '../../types';

const props = defineProps<{ tool: ToolStep; base?: number }>();
const icons = useIcons();

interface Fact { text: string; type: string; when: string; }

function fmtDate(s: string): string {
  return formatDate(s, undefined, '');
}

const facts = computed<Fact[]>(() => {
  const raw = props.tool.outputFull;
  if (!raw) return [];
  let data: unknown;
  try { data = JSON.parse(raw); } catch { return []; }
  const obj = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
  // search -> {hits}, about_user -> {facts}; tolerate a bare array too.
  const arr: unknown = Array.isArray(data)
    ? data
    : obj && Array.isArray(obj.hits)
      ? obj.hits
      : obj && Array.isArray(obj.facts)
        ? obj.facts
        : [];
  if (!Array.isArray(arr)) return [];
  return arr
    .filter((f): f is Record<string, unknown> => !!f && typeof f === 'object' && typeof f.fact === 'string')
    .map((f) => ({
      text: String(f.fact ?? ''),
      type: String(f.fact_type ?? ''),
      when: fmtDate(String(f.asserted_at ?? '')),
    }))
    .filter((f) => f.text.trim() !== '');
});
</script>

<template>
  <Fold v-if="facts.length">
    <template #summary>
      <component :is="icons.brain" :size="13" class="mem__icon" />
      <span>From memory</span>
      <span class="mem__count">{{ facts.length }}</span>
    </template>
    <ul class="mem__list" role="list">
      <li v-for="(f, i) in facts" :key="i" class="mem__item" :data-cite="(base ?? 0) + i + 1">
        <p class="mem__text">{{ f.text }}</p>
        <div v-if="f.type || f.when" class="mem__meta">
          <span v-if="f.type" class="mem__type">{{ f.type }}</span>
          <span v-if="f.when" class="mem__when">{{ f.when }}</span>
        </div>
      </li>
    </ul>
  </Fold>
</template>

<style scoped>
.mem__icon { flex: 0 0 auto; }
.mem__count { color: var(--ink-subtle); }

.mem__list {
  margin: var(--space-2) 0 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md, 10px);
  background: var(--surface-panel);
  overflow: hidden;
}
.mem__item { padding: var(--space-2) var(--space-3); }
.mem__item + .mem__item { border-top: 1px solid var(--border-subtle); }
.mem__text {
  margin: 0;
  font-size: var(--text-xs);
  line-height: 1.5;
  color: var(--ink-muted);
}
.mem__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-1) var(--space-2);
  margin-top: var(--space-1);
  font-size: var(--text-xs);
}
.mem__type {
  padding: 0 6px;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm, 6px);
  color: var(--ink-muted);
  text-transform: lowercase;
  letter-spacing: 0.01em;
}
.mem__when { color: var(--ink-subtle); }
</style>
